import { useFormContext } from '@calendly/form';
import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { EditPencilIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useEffect, useMemo, useState, forwardRef } from 'react';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { KINDS_WITH_MANDATORY_ATTRIBUTES, LocationSelect, SelectedLocation, InlineChooser } from 'components/event_type_edit';
import { roundRobinAvailableKinds, createLocationValues, useGetPropsForInline } from './utils';
const allOptionsWrapperCss = "a1ayllfp";
const AllOptionsWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(allOptionsWrapperCss, props.className)} />;
});
const styledDropdownIsPhoneCss = "s1g3aadz";
const styledDropdownCss = "sr6ug7p";
const StyledDropdown = forwardRef((props, ref) => {
  const fullWidth = true;
  const isPhone = useDeviceType('isPhone');
  return <ViewportDropdownMenu ref={ref} {...props} fullWidth={fullWidth} data-container="set-location-for-all-dropdown" data-component="set-location-for-all-dropdown" className={classNames(styledDropdownCss, {
    [styledDropdownIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledLocationSelectCss = "s78ps7q";
const StyledLocationSelect = forwardRef((props, ref) => <LocationSelect ref={ref} {...props} className={classNames(styledLocationSelectCss, props.className)} />);
const buttonContainerIsPhoneCss = "bd2iq3c";
const buttonContainerCss = "bv4ifub";
const ButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, {
    [buttonContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const inlineWrapperIsPhoneCss = "i1fqa8du";
const inlineWrapperCss = "i196ieqq";
const InlineWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(inlineWrapperCss, {
    [inlineWrapperIsPhoneCss]: isPhone
  }, props.className)} />;
});
export const AllHostsLocationSelect = ({
  onSelect,
  eventType
}) => {
  const [toggle, Dropdown] = useDropdown();
  const [selectedKind, setSelectedKind] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const {
    setFieldValue,
    values: {
      allHostsLocationSelection
    },
    errors: {
      allHostsLocationSelection: allHostsLocationSelectionErrors
    }
  } = useFormContext();
  const isPhone = useDeviceType('isPhone');
  const onCloseDropdown = () => {
    setSelectedKind(null);
    setSelectedLocation(null);
    toggle();
  };
  const onSelectKind = kind => {
    setSelectedKind(kind);
  };
  const onRemoveKind = () => {
    setSelectedKind(null);
  };
  const onApply = () => {
    onSelect(selectedLocation);
    onCloseDropdown();
  };
  const onKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onApply();
    }
  };
  const needsMoreInfo = useMemo(() => KINDS_WITH_MANDATORY_ATTRIBUTES.includes(selectedKind) || false, [selectedKind]);
  const isValid = useMemo(() => {
    if (!selectedKind) return false;
    const isMoreInfoRequired = needsMoreInfo && allHostsLocationSelectionErrors;
    return !needsMoreInfo || !isMoreInfoRequired;
  }, [selectedKind, needsMoreInfo, allHostsLocationSelectionErrors]);
  useEffect(() => {
    if (!selectedKind) {
      setSelectedLocation(null);
    } else if (isValid) {
      const values = createLocationValues(selectedKind, allHostsLocationSelection);
      setSelectedLocation(values);
    }
  }, [selectedKind, isValid, allHostsLocationSelection, setSelectedLocation]);
  useOnValueChange(needsMoreInfo, () => {
    setFieldValue('allHostsLocationSelection', {
      kind: selectedKind,
      location: '',
      phoneNumber: '',
      additionalInfo: '',
      hideLocation: selectedKind === 'custom' ? 'visible' : ''
    });
  });
  const propsForInline = useGetPropsForInline(selectedKind, eventType);
  return <AllOptionsWrapper>
      <MinimalButton onClick={toggle} size="small">
        <EditPencilIcon />
        {I18n.t('publisher.event_type_editor.sections.event_details.location.set_same_location')}
      </MinimalButton>
      <Dropdown>
        <StyledDropdown>
          {!selectedKind && <StyledLocationSelect availableKinds={roundRobinAvailableKinds} onChange={onSelectKind} startExpanded />}
          {selectedKind && <SelectedLocation kind={selectedKind} onRemove={onRemoveKind} />}
          {needsMoreInfo && <InlineWrapper>
              <InlineChooser eventType={eventType} fieldNamePrefix="allHostsLocationSelection" onKeyPress={onKeyPress} {...propsForInline} />
            </InlineWrapper>}
          <ButtonContainer>
            {!isPhone && <Button decoration="secondary-outline" size="small" onClick={onCloseDropdown}>Cancel</Button>}
            <Button size={!isPhone && 'small'} disabled={!isValid} onClick={onApply}>Apply</Button>
          </ButtonContainer>
        </StyledDropdown>
      </Dropdown>
    </AllOptionsWrapper>;
};

require("./all_hosts_location_select.wyw-in-js.css!=!../../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./all_hosts_location_select.js");